import type { AppError } from "@/shared/api";

export type CompanyErrorSurface =
  | "validation"
  | "notFound"
  | "server"
  | "network"
  | "forbidden"
  | "unknown";

export type CompanyErrorField = "name" | "address" | "phone" | "description";

export type CompanyErrorKey =
  | "salon.profile.errors.nameInvalid"
  | "salon.profile.errors.nameRequired"
  | "salon.profile.errors.addressTooLong"
  | "salon.profile.errors.phoneTooLong"
  | "salon.profile.errors.descriptionTooLong"
  | "salon.profile.errors.saveFailed"
  | "salon.profile.errors.notFound"
  | "errors.network"
  | "errors.server"
  | "errors.forbidden"
  | "errors.unknown";

export interface MappedCompanyError {
  surface: CompanyErrorSurface;
  i18nKey: CompanyErrorKey;
  field?: CompanyErrorField;
}

/**
 * Адаптер `AppError → {surface, i18nKey, field?}` (spec §7.4). Чиста функція —
 * без router/store/toast/i18n; усе застосовує викликач (composable).
 */
export function mapCompanyError(e: AppError): MappedCompanyError {
  switch (e.kind) {
    case "validation":
      if (e.code === "invalid_company_name") {
        return { surface: "validation", field: "name", i18nKey: "salon.profile.errors.nameInvalid" };
      }
      if (e.code === "invalid_address") {
        return { surface: "validation", field: "address", i18nKey: "salon.profile.errors.addressTooLong" };
      }
      if (e.code === "invalid_phone") {
        return { surface: "validation", field: "phone", i18nKey: "salon.profile.errors.phoneTooLong" };
      }
      if (e.code === "invalid_description") {
        return { surface: "validation", field: "description", i18nKey: "salon.profile.errors.descriptionTooLong" };
      }
      // 400 invalid_body або невідомий validation-код
      return { surface: "validation", i18nKey: "salon.profile.errors.saveFailed" };
    case "notFound":
      return { surface: "notFound", i18nKey: "salon.profile.errors.notFound" };
    case "server":
      // 413 payload_too_large → saveFailed; 5xx → errors.server
      if (e.status === 413) {
        return { surface: "server", i18nKey: "salon.profile.errors.saveFailed" };
      }
      return { surface: "server", i18nKey: "errors.server" };
    case "network":
      return { surface: "network", i18nKey: "errors.network" };
    case "forbidden":
      return { surface: "forbidden", i18nKey: "errors.forbidden" };
    default:
      return { surface: "unknown", i18nKey: "errors.unknown" };
  }
}
